import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, X } from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
  { k: "varsayilan_yemek", label: "Günlük yemek tutarı", admin: true },
  { k: "yemek_yok", label: "Yemek ücreti yok işareti", admin: true },
  { k: "yol_yok", label: "Yol ücreti yok işareti", admin: true },
  { k: "calisma_gunleri", label: "Çalışma günleri (yarım günler dahil)", admin: false },
];
const GUN = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const describe = (s, k) => {
  if (k === "varsayilan_yemek") return s.varsayilan_yemek != null ? `${s.varsayilan_yemek} ₺` : "boş";
  if (k === "calisma_gunleri") {
    const l = [...(s.calisma_gunleri || []).map((d) => GUN[d]), ...(s.yarim_gunler || []).map((d) => `${GUN[d]}½`)];
    return l.length ? l.join(" ") : "şablona bağlı";
  }
  return s[k] ? "işaretli" : "kapalı";
};

export const PositionCopyModal = ({ source, templates, isAdmin, onClose, onDone, onPreview }) => {
  const allowed = FIELDS.filter((f) => isAdmin || !f.admin);
  const [fields, setFields] = useState(new Set(allowed.map((f) => f.k)));
  const [targets, setTargets] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const others = templates.filter((t) => t.id !== source.id);
  const tog = (set, setter, k) => { const n = new Set(set); if (n.has(k)) n.delete(k); else n.add(k); setter(n); };

  const submit = () => {
    const vals = {};
    fields.forEach((k) => {
      vals[k] = source[k] ?? (k === "calisma_gunleri" ? [] : k === "varsayilan_yemek" ? null : false);
      if (k === "calisma_gunleri") vals.yarim_gunler = source.yarim_gunler || [];
    });
    onPreview({ title: `"${source.isim}" ayarları → ${targets.size} pozisyon`,
      changes: Object.fromEntries([...targets].map((id) => [id, vals])), run: doSubmit });
  };
  const doSubmit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/position-templates/${source.id}/copy-settings`, { target_ids: [...targets], fields: [...fields] });
      toast.success(`${r.data.updated} pozisyon güncellendi`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kopyalanamadı"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 grid place-items-center p-4" onClick={(e) => e.stopPropagation()} data-testid="position-copy-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-bold text-sm flex items-center gap-1.5"><Copy size={14} /> "{source.isim}" ayarlarını kopyala</div>
          <button onClick={onClose} data-testid="position-copy-close" className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14} /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <div>
            <div className="text-xs font-bold text-slate-700 mb-1.5">Kopyalanacak alanlar</div>
            {allowed.map((f) => (
              <label key={f.k} className="flex items-center gap-2 text-sm py-0.5">
                <input type="checkbox" checked={fields.has(f.k)} onChange={() => tog(fields, setFields, f.k)} data-testid={`copy-field-${f.k}`} />
                {f.label} <span className="text-[11px] text-slate-500">· {describe(source, f.k)}</span>
              </label>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-bold text-slate-700">Hedef pozisyonlar</div>
              <button type="button" onClick={() => setTargets(targets.size === others.length ? new Set() : new Set(others.map((t) => t.id)))}
                data-testid="copy-target-all" className="text-[11px] font-semibold text-teal-700 hover:underline">{targets.size === others.length ? "Hiçbiri" : "Tümü"}</button>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {others.map((t) => (
                <label key={t.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${targets.has(t.id) ? "border-teal-400 bg-teal-50" : "border-slate-200"}`}>
                  <input type="checkbox" checked={targets.has(t.id)} onChange={() => tog(targets, setTargets, t.id)} data-testid={`copy-target-${t.id}`} />
                  <span className="font-semibold truncate">{t.isim}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">İptal</button>
          <button onClick={submit} disabled={busy || !fields.size || !targets.size} data-testid="position-copy-submit"
            className="px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-semibold disabled:opacity-50">Önizle & Kopyala</button>
        </div>
      </div>
    </div>
  );
};
